import { GetApiPayloadByUrl } from '@crossbeam/openapi';
import { MarkOptional } from '@crossbeam/types';

import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import useAuth from '@/composables/useAuth';
import { captureException } from '@/errors';
import {
  useBillingStore,
  useFeedsStore,
  useFlashesStore,
  useIntegrationProfileStore,
  usePartnerCloudStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import {
  IntegrationProfile,
  IntegrationProfileType,
  MinimalIntegrationProfile,
} from '@/types/integration_profiles';
import { STANDARD_POPULATIONS } from '@/types/populations';

type IntegrationProfileOptional = MarkOptional<
  IntegrationProfile,
  'profile_id'
>;
export default function useIntegrationProfile(
  profileType: IntegrationProfileType | undefined = undefined,
  options = { initOnMount: true },
) {
  const defaultStandardPopSettings = (
    [...STANDARD_POPULATIONS, 'custom'] as const
  ).map((pop) => ({
    standard_type: pop,
    is_included: ['customers', 'open_opportunities'].includes(pop),
  }));

  const { hasPermission } = useAuth();
  const integrationProfileStore = useIntegrationProfileStore();
  const { getFeedById } = useFeedsStore();
  const flashesStore = useFlashesStore();
  const { getSourceById } = useSourcesStore();
  const partnerCloudStore = usePartnerCloudStore();
  const route = useRoute();

  const excludePartnershipsCreatedSince = ref<string | undefined>(undefined);
  const isLoading = ref(false);
  const isSettingDirty = ref(false);
  const integrationProfileSettings = ref<IntegrationProfileOptional | null>(
    null,
  );
  const existingIntegrationProfileSettings =
    ref<IntegrationProfileOptional | null>(null);

  const { populations, partnerPopulations } = storeToRefs(
    usePopulationsStore(),
  );
  const { isFreeTier, isSupernodeOrEnterpriseTier } =
    storeToRefs(useBillingStore());

  const integrationDescription = computed(() => {
    return profileType === 'clari'
      ? 'Configure the populations syncing to Clari. You can only sync Salesforce based populations and Partner Opps or Partner Customers.'
      : `Amplify and activate your ecosystem insights by pushing overlapping partner data into the Crossbeam overlaps custom object in ${friendlyName.value}.`;
  });

  const enableDescription = computed(() => {
    // todo confirm copy
    return `Push overlap data to ${friendlyName.value}. This will impact your record export limit`;
  });

  const hasPermissionToManageIntegrations = computed(() =>
    hasPermission('manage:integrations'),
  );

  const isIntegrationAvailable = computed(() =>
    profileType === 'hubspot'
      ? !isFreeTier.value
      : isSupernodeOrEnterpriseTier.value,
  );

  type Integration = 'salesforce' | 'gong' | 'hubspot' | 'outreach' | 'chrome';
  const integrationType = computed<Integration>(
    () => route.params.integration as Integration,
  );

  const friendlyNameMap = {
    salesforce: 'Salesforce',
    gong: 'Gong',
    hubspot: 'HubSpot',
    outreach: 'Outreach',
    chrome: 'Chrome',
  } as const;

  const friendlyName = computed(() => {
    if (!profileType || profileType === 'webhook') return null;
    if (profileType === 'crossbeam_copilot')
      return integrationType.value
        ? friendlyNameMap[integrationType.value]
        : null;
    const names = {
      clari: 'Clari',
      salesforce: 'Salesforce',
      hubspot: 'HubSpot',
      microsoft_dynamics: 'MS Dynamics',
      snowflake: 'Snowflake',
      databricks: 'Databricks',
    };
    return names[profileType];
  });

  onMounted(async () => {
    if (
      profileType &&
      !integrationProfileSettings.value &&
      options.initOnMount
    ) {
      await initProfileSettings();
    }
  });

  const onSettingsUpdated = (payload: MinimalIntegrationProfile) => {
    isSettingDirty.value = true;
    if (!integrationProfileSettings.value) return;
    integrationProfileSettings.value = {
      ...integrationProfileSettings.value,
      ...payload,
    };
  };
  function onUpdateExcludePartnershipsCreatedSince(isChecked: boolean) {
    isSettingDirty.value = true;
    if (!integrationProfileSettings.value) return;
    integrationProfileSettings.value = {
      ...integrationProfileSettings.value,
      exclude_partnerships_created_since: isChecked
        ? null
        : (excludePartnershipsCreatedSince.value ?? null),
    };
  }

  const isSaving = ref(false);
  const saveIntegrationProfile = async () => {
    if (!integrationProfileSettings.value) {
      return;
    }
    isSaving.value = true;

    if (!integrationProfileSettings.value) {
      return;
    }

    const allPopsSet = new Set([
      ...partnerPopulations.value.map((p) => p.id),
      ...populations.value.map((p) => p.id),
    ]);
    // if there is no corresponding population, it must have been deleted or unshared, we don't want it in our settings
    const validPopSettings =
      integrationProfileSettings.value.population_settings.filter((p) =>
        allPopsSet.has(p.population_id),
      );

    const outPayload = {
      exclude_partnerships_created_since: integrationProfileSettings.value
        .exclude_partnerships_created_since
        ? excludePartnershipsCreatedSince.value
        : null,
      population_settings: validPopSettings.map((p) => ({
        population_id: p.population_id,
        is_partner_population: p.is_partner_population,
        is_included: p.is_included,
      })),
      standard_population_settings:
        integrationProfileSettings.value.standard_population_settings.map(
          (p) => ({
            standard_type: p.standard_type,
            is_included: p.is_included,
          }),
        ),
    };

    const isExcludePartnershipsCreatedSinceChanged =
      !!integrationProfileSettings.value?.exclude_partnerships_created_since !==
      !!existingIntegrationProfileSettings.value
        ?.exclude_partnerships_created_since;

    try {
      if (!profileType) return;
      integrationProfileSettings.value?.profile_id
        ? await integrationProfileStore.putIntegrationProfileSettings(
            integrationProfileSettings.value?.profile_id,
            outPayload,
          )
        : await integrationProfileStore.postIntegrationProfileSettings({
            ...outPayload,
            integration_type: profileType,
          });

      await integrationProfileStore.refreshIntegrationProfileStore();

      flashesStore.addSuccessFlash({
        message: `${
          profileType === 'crossbeam_copilot'
            ? 'Crossbeam Copilot'
            : friendlyName.value
        } settings saved`,
        description: isExcludePartnershipsCreatedSinceChanged
          ? integrationProfileSettings.value?.exclude_partnerships_created_since
            ? 'Partners will no longer be added automatically'
            : 'New partners will be automatically added'
          : profileType === 'crossbeam_copilot'
            ? undefined
            : `Your integration configuration has been saved`,
      });
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: `Your ${profileType === 'crossbeam_copilot' ? 'Crossbeam Copilot' : 'integration'} settings could not be saved`,
      });
    } finally {
      isSaving.value = false;
    }
  };

  const clearProfilesState = () => {
    partnerCloudStore.$patch({ inProgressInstallation: null });
    integrationProfileSettings.value = null;
    existingIntegrationProfileSettings.value = null;
  };

  const setIntegrationProfileDefaults = (
    integrationProfile: IntegrationProfile,
  ) => {
    const standardPopSettingsRaw = [
      ...integrationProfile.standard_population_settings,
      ...defaultStandardPopSettings.filter(
        (pop) =>
          !integrationProfile?.standard_population_settings.some(
            (p) => p.standard_type === pop.standard_type,
          ),
      ),
    ];

    integrationProfileSettings.value = {
      ...integrationProfile,
      standard_population_settings: standardPopSettingsRaw,
      population_settings: integrationProfile.population_settings,
    };
    existingIntegrationProfileSettings.value = integrationProfileSettings.value;
  };

  const initProfileSettings = async () => {
    if (!profileType) return;
    await integrationProfileStore.readySync;
    const integrationProfile =
      integrationProfileStore.getIntegrationProfileByType(profileType);
    // on initial fetch, we set the date to now
    excludePartnershipsCreatedSince.value = DateTime.now().toISO();

    if (integrationProfile) {
      setIntegrationProfileDefaults(integrationProfile);
      return;
    }

    // If there isn't an appropriate integration profile, make a new one and post it.
    const popSettings = populations.value
      .filter((pop) => {
        const { feed_id: feedId } = getSourceById(pop.source_id) as unknown as {
          feed_id: number;
        };
        const feed = getFeedById(feedId);
        return feed?.integration.type === profileType;
      })
      .map((pop) => {
        return {
          population_id: pop.id,
          is_partner_population: false,
          is_included: true,
        };
      });

    const payload: GetApiPayloadByUrl<'/v0.1/integration-profiles', 'post'> = {
      integration_type: profileType,
      population_settings: popSettings,
      standard_population_settings: defaultStandardPopSettings,
    };

    isLoading.value = true;

    // short circuit the POST for snowflake and databricks, don't create profile until save is clicked
    if (profileType === 'snowflake' || profileType === 'databricks') {
      integrationProfileSettings.value = {
        ...payload,
        exclude_partnerships_created_since: null,
      };
      return;
    }

    try {
      const { data, error } =
        await integrationProfileStore.postIntegrationProfileSettings(payload);
      if (error) throw error;

      integrationProfileSettings.value = data;
      existingIntegrationProfileSettings.value =
        integrationProfileSettings.value;
      await integrationProfileStore.refreshIntegrationProfileStore();
    } catch (err) {
      if (err instanceof Error) {
        flashesStore.addErrorFlash({ message: err.message });
      }
    } finally {
      isLoading.value = false;
    }
  };

  return {
    clearProfilesState,
    enableDescription,
    excludePartnershipsCreatedSince,
    friendlyName,
    hasPermissionToManageIntegrations,
    integrationDescription,
    integrationProfileSettings,
    isIntegrationAvailable,
    isLoading,
    onSettingsUpdated,
    onUpdateExcludePartnershipsCreatedSince,
    saveIntegrationProfile,
    initProfileSettings,
    isSaving,
    isSettingDirty,
  };
}
